import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API } from "@/lib/api";
import { Link } from "react-router-dom";
import { UploadCloud, FileText, Loader2, CheckCircle2, Clock, AlertTriangle, Sparkles } from "lucide-react";

const STATUS_ICON = {
  completed: <CheckCircle2 size={14} className="text-[#3A5A40]" strokeWidth={2} />,
  processing: <Loader2 size={14} className="animate-spin text-[#B5953B]" strokeWidth={2} />,
  pending: <Clock size={14} className="text-[#5C5C54]" strokeWidth={2} />,
  failed: <AlertTriangle size={14} className="text-[#9E2A2B]" strokeWidth={2} />,
};

export default function AdminProductBulkUpload() {
  const [uploading, setUploading] = useState(false);
  const [createdIds, setCreatedIds] = useState([]); // [{id, filename}]
  const [items, setItems] = useState([]); // refreshed product docs

  // Poll for status of items in the current batch
  useEffect(() => {
    if (createdIds.length === 0) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/products", { params: { limit: 200 } });
        const map = new Map(data.map((p) => [p.id, p]));
        setItems(createdIds.map((c) => map.get(c.id)).filter(Boolean));
      } catch (e) {
        /* ignore */
      }
    };
    tick();
    const handle = setInterval(tick, 5000);
    return () => clearInterval(handle);
  }, [createdIds]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const token = localStorage.getItem("heda_token");
      const res = await fetch(`${API}/products/bulk-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt);
      }
      const data = await res.json();
      setCreatedIds(data.products || []);
    } catch (e) {
      alert(`Bulk upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const total = items.length;
  const completed = items.filter((i) => i.extraction_status === "completed").length;
  const failed = items.filter((i) => i.extraction_status === "failed").length;
  const processing = items.filter((i) => i.extraction_status === "processing").length;

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="hed-overline">Catalog · bulk import</div>
        <h1 className="font-serif text-4xl mt-2 tracking-tight">Upload many brochures at once.</h1>
        <p className="text-sm text-[#5C5C54] mt-2 max-w-2xl">
          Drop a folder of PDFs. Each one becomes its own draft product. Hedaply runs AI extraction
          + web research in the background — come back later to review and publish.
        </p>
      </div>

      <div className="hed-card p-6 md:p-8">
        <label
          className="block border border-dashed border-[#E8E6DF] hover:bg-[#F2F0EA] cursor-pointer text-center py-16 px-4 rounded-sm transition-colors"
          data-testid="bulk-dropzone"
        >
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
            data-testid="bulk-file-input"
          />
          <UploadCloud size={32} strokeWidth={1.5} className="mx-auto text-[#5C5C54] mb-3" />
          <div className="font-semibold">Drop PDFs or click to upload</div>
          <div className="text-xs text-[#5C5C54] mt-2">
            Multi-select supported · up to 50 MB per file · processing runs in the background
          </div>
          {uploading && <div className="text-xs text-[#B5953B] mt-3">Uploading...</div>}
        </label>
      </div>

      {items.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Batch progress</h2>
            <div className="text-xs text-[#5C5C54]">
              {completed} of {total} ready · {processing} extracting · {failed} failed
            </div>
          </div>
          <div className="hed-card divide-y divide-[#E8E6DF]">
            {items.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 px-5 py-3"
                data-testid={`batch-item-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText size={16} className="text-[#5E6033] flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {p.name || p.files?.[0]?.original_filename || "Untitled"}
                    </div>
                    <div className="text-[11px] text-[#5C5C54] flex items-center gap-2">
                      {STATUS_ICON[p.extraction_status] || STATUS_ICON.pending}
                      <span>
                        {p.extraction_status === "completed" && p.code && `Code: ${p.code}`}
                        {p.extraction_status === "processing" && "Extracting + researching..."}
                        {p.extraction_status === "pending" && "Queued"}
                        {p.extraction_status === "failed" && "Extraction failed — open to fix"}
                      </span>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/admin/products/${p.id}/edit`}
                  className="text-[11px] hed-overline hover:text-[#B5953B]"
                  data-testid={`batch-open-${p.id}`}
                >
                  Open
                </Link>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-[#5C5C54] mt-3 flex items-center gap-1.5">
            <Sparkles size={11} strokeWidth={2} className="text-[#B5953B]" />
            Each item also gets enriched via web research (Perplexity Sonar). This may take up to a minute per product.
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
